<template>
    <div class="product-structure-row">
        <div
            class="product-structure-row__identity"
        >
            <span
                v-if="entry.depth > 0"
                class="product-structure-row__guides"
                aria-hidden="true"
            >
                <span
                    v-for="(continues, index) in entry.ancestorContinuations"
                    :key="`ancestor-${index}`"
                    class="product-structure-row__guide"
                    :class="{ 'product-structure-row__guide--continuation': continues }"
                />
                <span
                    class="product-structure-row__guide product-structure-row__guide--branch"
                    :class="{
                        'product-structure-row__guide--last': entry.isLastSibling,
                    }"
                />
            </span>

            <b-button
                v-if="hasChildren && !entry.isRoot"
                class="product-structure-row__product-icon"
                variant="flat-secondary"
                :aria-label="expanded ? 'Collapse component' : 'Expand component'"
                @click="$emit('toggle', entry)"
            >
                <feather-icon icon="BoxIcon" size="16" />
                <feather-icon
                    class="product-structure-row__toggle-marker"
                    :icon="expanded ? 'ChevronDownIcon' : 'ChevronRightIcon'"
                    size="10"
                />
            </b-button>
            <span
                v-else
                class="product-structure-row__product-icon"
                :class="{
                    'product-structure-row__product-icon--root-parent': entry.isRoot && hasChildren,
                }"
                aria-hidden="true"
            >
                <feather-icon icon="BoxIcon" size="16" />
            </span>

            <span class="product-structure-row__copy">
                <span class="product-structure-row__title">{{ entry.node.title }}</span>
                <span v-if="metadata" class="product-structure-row__metadata">
                    {{ metadata }}
                </span>
            </span>

            <b-badge v-if="entry.isRoot" variant="light-success" class="ml-1">
                {{ translate(
                    'Page(ReceivedRequest):Component(ProductStructure):main-product',
                    'Main product'
                ) }}
            </b-badge>
        </div>

        <div class="product-structure-row__actions">
            <b-spinner v-if="busy" small variant="primary" class="mr-1" />

            <b-button
                size="sm"
                variant="outline-primary"
                :disabled="busy"
                @click="$emit('add', entry)"
            >
                <feather-icon icon="PlusIcon" class="mr-25" />
                {{ translate(
                    'Page(ReceivedRequest):Component(ProductStructure):add-component',
                    'Add component'
                ) }}
            </b-button>

            <template
                v-if="!entry.isRoot"
            >
                <b-button
                    class="product-structure-row__edit"
                    size="sm"
                    variant="outline-secondary"
                    :disabled="busy"
                    @click="$emit('edit', entry)"
                >
                    {{ translate('Component(Button):edit', 'Edit') }}
                </b-button>
                <b-button
                    class="product-structure-row__remove"
                    size="sm"
                    variant="flat-danger"
                    :disabled="busy"
                    :aria-label="translate('Component(Button):remove', 'Remove')"
                    :title="translate('Component(Button):remove', 'Remove')"
                    @click="$emit('remove', entry)"
                >
                    <feather-icon icon="Trash2Icon" />
                </b-button>
            </template>
        </div>
    </div>
</template>

<script>
import {
    BBadge,
    BButton,
    BSpinner,
} from 'bootstrap-vue'

export default {
    name: 'ProductStructureTreeRow',
    components: {
        BBadge,
        BButton,
        BSpinner,
    },
    props: {
        entry: { type: Object, required: true },
        expanded: { type: Boolean, default: true },
        busy: { type: Boolean, default: false },
    },
    computed: {
        hasChildren() {
            return Array.isArray(this.entry.node.children) && this.entry.node.children.length > 0
        },
        metadata() {
            const metadata = []
            const supplier = this.entry.node.supplier
                || (Array.isArray(this.entry.node.suppliers) && this.entry.node.suppliers[0])

            if (supplier && supplier.title) {
                metadata.push(supplier.title)
            }

            if (!this.entry.isRoot && this.entry.node.numberOfUnits) {
                metadata.push(this.translate(
                    'Page(ReceivedRequest):Component(Text):component-number-of-units',
                    `${this.entry.node.numberOfUnits} unit(s)`,
                    { numberOfUnits: this.entry.node.numberOfUnits },
                ))
            }

            return metadata.join(' · ')
        },
    },
    methods: {
        translate(key, fallback, parameters = {}) {
            const translation = this.$t(key, parameters)
            return translation && translation !== key ? translation : fallback
        },
    },
}
</script>

<style lang="scss" scoped>
.product-structure-row {
    display: flex;
    min-height: 3rem;
    align-items: center;
    justify-content: space-between;
    padding: .45rem .75rem;
    border-bottom: 1px solid #ebe9f1;
    background: #fff;
}

.product-structure-row:last-child {
    border-bottom: 0;
}

.product-structure-row__identity,
.product-structure-row__actions {
    display: flex;
    align-items: center;
}

.product-structure-row__identity {
    min-width: 0;
    flex: 1 1 auto;
}

.product-structure-row__guides {
    display: inline-flex;
    align-self: stretch;
    flex: 0 0 auto;
}

.product-structure-row__guide {
    position: relative;
    width: 2rem;
    flex: 0 0 2rem;
}

.product-structure-row__guide--continuation::before,
.product-structure-row__guide--branch::before,
.product-structure-row__guide--branch::after {
    position: absolute;
    background: #d8d6de;
    content: '';
}

.product-structure-row__guide--continuation::before {
    top: -.45rem;
    bottom: -.45rem;
    left: 50%;
    width: 1px;
}

.product-structure-row__guide--branch::before {
    top: -.45rem;
    bottom: -.45rem;
    left: 50%;
    width: 1px;
}

.product-structure-row__guide--branch.product-structure-row__guide--last::before {
    bottom: 50%;
}

.product-structure-row__guide--branch::after {
    top: 50%;
    right: 0;
    left: 50%;
    height: 1px;
}

.product-structure-row__actions {
    flex: 0 0 auto;
    gap: .5rem;
    margin-left: 1rem;
}

.product-structure-row__product-icon {
    position: relative;
    display: inline-flex;
    width: 2rem;
    height: 2rem;
    flex: 0 0 2rem;
    align-items: center;
    justify-content: center;
    margin-right: .65rem;
    border: 1px solid #ebe9f1;
    border-radius: .357rem;
    color: #6e6b7b;
}

.product-structure-row__product-icon.btn {
    padding: 0;
}

.product-structure-row__toggle-marker {
    position: absolute;
    right: .1rem;
    bottom: .05rem;
    border-radius: 50%;
    background: #fff;
}

.product-structure-row__product-icon--root-parent::after {
    position: absolute;
    top: 100%;
    left: 50%;
    width: 1px;
    height: .55rem;
    background: #d8d6de;
    content: '';
}

.product-structure-row__copy {
    display: flex;
    min-width: 0;
    flex-direction: column;
}

.product-structure-row__title {
    overflow: hidden;
    color: #2f2c52;
    font-weight: 600;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.product-structure-row__metadata {
    margin-top: .1rem;
    color: #8b8996;
    font-size: .8rem;
}

@media (max-width: 767.98px) {
    .product-structure-row {
        align-items: flex-start;
    }

    .product-structure-row__actions {
        flex-direction: column;
        align-items: flex-end;
    }
}
</style>
